class EcomExpertsMethod extends HTMLElement {
  constructor() {
    super();
    this.section = null;
    this.stepsContainer = null;
    this.scrollDot = null;
    this.steps = null;
    this.scrollLine = null;
    this.button = null;
    this.arrow = null;
  }

  connectedCallback() {
    // Find the section by data attribute or class
    const sectionId = this.getAttribute('data-section-id');
    if (sectionId) {
      this.section = document.querySelector(`[data-section-id="${sectionId}"]`);
    } else {
      this.section = document.querySelector('.ecomexperts-method');
    }

    if (!this.section) return;

    this.stepsContainer = this.section.querySelector('.method-steps');
    this.scrollDot = this.section.querySelector('.method-scroll-dot');
    this.steps = this.section.querySelectorAll('.method-step');
    this.scrollLine = this.section.querySelector('.method-scroll-line');
    this.button = this.section.querySelector('.method-button--mobile'); // Target mobile button
    this.arrow = this.section.querySelector('.method-arrow');

    this.init();
  }

  disconnectedCallback() {
    // Clean up event listeners if needed
    if (this.scrollHandler) {
      window.removeEventListener('scroll', this.scrollHandler);
    }
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler);
    }
  }

  init() {
    // Ensure first step is always visible
    if (this.steps.length > 0) {
      this.steps[0].classList.add('method-step--visible');
    }

    // Hide dot initially
    if (this.scrollDot) {
      this.scrollDot.style.opacity = '0';
      this.scrollDot.style.pointerEvents = 'none';
    }

    // Position dot horizontally aligned with the line
    this.positionDot();

    this.setupScrollObserver();
    this.handleScroll();

    // Use throttled scroll for better performance
    let ticking = false;
    this.scrollHandler = () => {
      if (!ticking) {
        window.requestAnimationFrame(() => {
          this.handleScroll();
          ticking = false;
        });
        ticking = true;
      }
    };

    this.resizeHandler = () => {
      this.positionDot();
      this.handleScroll();
    };

    window.addEventListener('scroll', this.scrollHandler, { passive: true });
    window.addEventListener('resize', this.resizeHandler);
  }

  positionDot() {
    if (!this.scrollLine || !this.scrollDot) return;

    // Get the line's position relative to viewport
    const lineRect = this.scrollLine.getBoundingClientRect();
    const lineLeft = lineRect.left + (lineRect.width / 2); // Center of the line
    const lineTop = lineRect.top;
    const lineBottom = lineRect.bottom;
    const lineHeight = lineRect.height;

    // Set dot's horizontal position to match the line
    this.scrollDot.style.left = `${lineLeft}px`;

    // Constrain dot's vertical position to stay within line bounds
    const viewportCenter = window.innerHeight / 2; // 50vh
    const dotHeight = 38; // Actual height of the dot
    const dotHalfHeight = dotHeight / 2;

    // Calculate desired position (center of viewport)
    let desiredTop = viewportCenter - dotHalfHeight;

    // Add padding from edges to prevent touching the boundaries
    const topPadding = 40;
    const bottomPadding = 60;

    // Constrain to line bounds with padding
    const minTop = lineTop + topPadding;
    const maxTop = lineBottom - dotHeight - bottomPadding;

    // Apply strict constraints
    let constrainedTop = desiredTop;
    if (constrainedTop < minTop) {
      constrainedTop = minTop;
    } else if (constrainedTop > maxTop) {
      constrainedTop = maxTop;
    }

    // Only update if line is visible and has valid bounds
    // Stricter visibility check
    if (lineHeight > 0 && lineTop < (window.innerHeight - 50) && lineBottom > 50) {
      // Dot is constrained within line bounds
      this.scrollDot.style.top = `${constrainedTop}px`;
      this.scrollDot.style.transform = 'translate(-50%, 0)'; // Only translate X, not Y
    } else {
      // If line is not visible, hide dot completely
      this.scrollDot.style.opacity = '0';
      this.scrollDot.style.pointerEvents = 'none';
    }
  }

  setupScrollObserver() {
    const observerOptions = {
      root: null,
      rootMargin: '0px',
      threshold: [0, 0.1, 0.2, 0.3, 0.4, 0.5, 0.6, 0.7, 0.8, 0.9, 1]
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        // Only show dot when section is significantly in view (at least 30% visible)
        if (entry.isIntersecting && entry.intersectionRatio > 0.3) {
          // Additional check: ensure scroll line is also visible
          if (this.scrollLine) {
            const lineRect = this.scrollLine.getBoundingClientRect();
            const viewportHeight = window.innerHeight;
            
            // Only show dot if the line is meaningfully visible in viewport with strict margins
            const lineInView = lineRect.top < viewportHeight - 150 && lineRect.bottom > 150;
            
            if (lineInView && this.scrollDot) {
              this.scrollDot.style.opacity = '1';
              this.scrollDot.style.pointerEvents = 'auto';
            }
          }
          this.handleScroll();
        } else {
          // Hide dot when section is barely visible or out of view
          if (this.scrollDot) {
            this.scrollDot.style.opacity = '0';
            this.scrollDot.style.pointerEvents = 'none';
          }
        }
      });
    }, observerOptions);

    if (this.section) {
      observer.observe(this.section);
    }
  }

  handleScroll() {
    if (!this.stepsContainer || !this.scrollDot || !this.scrollLine || this.steps.length === 0) return;

    const sectionRect = this.section.getBoundingClientRect();
    const viewportHeight = window.innerHeight;
    const isMobile = window.innerWidth <= 767;

    // Check if section is meaningfully in viewport
    const sectionTop = sectionRect.top;
    const sectionBottom = sectionRect.bottom;
    const sectionHeight = sectionRect.height;
    
    // Check if scroll line is visible in viewport (strict check)
    const lineRect = this.scrollLine.getBoundingClientRect();
    const lineTop = lineRect.top;
    const lineBottom = lineRect.bottom;
    
    // Dot only visible when the line is meaningfully in view
    // Stricter margins: Keep 150px margin from top/bottom of viewport
    const topMargin = 150;
    const bottomMargin = 150;
    const isLineInView = lineTop < (viewportHeight - bottomMargin) && lineBottom > topMargin;
    
    // Section must also be substantially in view (at least 30% visible)
    const sectionVisibleHeight = Math.min(sectionBottom, viewportHeight) - Math.max(sectionTop, 0);
    const sectionVisibilityRatio = sectionVisibleHeight / sectionHeight;
    const isSectionInView = sectionVisibilityRatio > 0.3;

    // Show/hide dot based on strict visibility check
    if (isSectionInView && isLineInView) {
      this.scrollDot.style.opacity = '1';
      this.scrollDot.style.pointerEvents = 'auto';
    } else {
      this.scrollDot.style.opacity = '0';
      this.scrollDot.style.pointerEvents = 'none';
      return; // Exit early if section/line not properly in view
    }

    // Calculate scroll progress through the section

    // Calculate scroll progress (0 to 1)
    let scrollProgress = 0;

    if (sectionTop <= viewportHeight && sectionBottom >= 0) {
      // Calculate progress: 0 when section enters viewport, 1 when section fully scrolled past
      const scrollStart = viewportHeight;
      const scrollEnd = -sectionHeight + viewportHeight;
      const currentScroll = sectionTop;

      scrollProgress = Math.max(0, Math.min(1, (scrollStart - currentScroll) / (scrollStart - scrollEnd)));
    }

    // Dot stays fixed at center - no movement needed
    // Only update horizontal position if needed (for responsive)
    this.positionDot();

    // Show steps progressively with opacity based on scroll position
    let allStepsVisible = true;
    this.steps.forEach((step, index) => {
      // First step is always fully visible
      if (index === 0) {
        step.style.opacity = '1';
        step.style.transform = 'translateY(0)';
        return;
      }

      // Get step position relative to viewport
      const stepRect = step.getBoundingClientRect();
      const stepTop = stepRect.top;
      const stepBottom = stepRect.bottom;
      const stepHeight = stepRect.height;

      // Calculate opacity based on scroll position
      // Step starts fading in when it reaches 80% down the viewport
      // Fully visible when step center reaches 50% of viewport
      const fadeStart = viewportHeight * 0.8;
      const fadeEnd = viewportHeight * 0.5;

      let opacity = 0;
      let translateY = 30;

      if (stepTop < fadeStart) {
        // Step is entering the fade zone
        const distanceFromStart = fadeStart - stepTop;
        const fadeRange = fadeStart - fadeEnd;

        // Calculate opacity: 0 at fadeStart, 1 at fadeEnd
        opacity = Math.min(1, Math.max(0, distanceFromStart / fadeRange));

        // Calculate transform: translateY(30px) at start, translateY(0) at end
        translateY = 30 * (1 - opacity);
      }

      // Apply opacity and transform
      step.style.opacity = opacity.toString();
      step.style.transform = `translateY(${translateY}px)`;

      if (opacity < 1) {
        allStepsVisible = false;
      }
    });

    // On mobile: animate button based on last step position (stable reference)
    if (isMobile && this.button && this.steps.length > 0) {
      const lastStep = this.steps[this.steps.length - 1];
      const lastStepRect = lastStep.getBoundingClientRect();
      const lastStepBottom = lastStepRect.bottom;
      
      // Button fades in after the last step
      // More generous fade range to prevent flickering
      const fadeStart = viewportHeight * 0.7; // Start earlier
      const fadeEnd = viewportHeight * 0.3;   // End later
      
      let buttonOpacity = 0;
      let buttonTranslateY = 30;
      
      // Base calculation on last step's bottom position (stable reference)
      if (lastStepBottom < fadeStart) {
        // Calculate based on how far the last step has scrolled past fadeStart
        const distanceFromStart = fadeStart - lastStepBottom;
        const fadeRange = fadeStart - fadeEnd;
        
        // Calculate opacity: 0 at fadeStart, 1 at fadeEnd
        buttonOpacity = Math.min(1, Math.max(0, distanceFromStart / fadeRange));
        
        // Calculate transform: translateY(30px) at start, translateY(0) at end
        buttonTranslateY = 30 * (1 - buttonOpacity);
      }
      
      // Apply styles directly (keep button in layout, just control opacity)
      this.button.style.opacity = buttonOpacity.toString();
      this.button.style.transform = `translateY(${buttonTranslateY}px)`;
      
      // Arrow appears after button is mostly visible
      if (this.arrow) {
        if (buttonOpacity >= 0.9) {
          this.arrow.classList.add('method-arrow--visible');
        } else {
          this.arrow.classList.remove('method-arrow--visible');
        }
      }
    }
  }
}

customElements.define('ecomexperts-method', EcomExpertsMethod);

